import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { AuthService } from "app/core/auth/service/auth.service";
import { AccountService } from "app/core/services/account.service";
import { LanguageService } from "app/core/services/language.service";
import { LoaderService } from "app/shared/components/loader/loader.service";
import { Account } from "app/shared/models/account";
import {
  catchError,
  EMPTY,
  map,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil,
} from "rxjs";

@Component({
  selector: "account-selection",
  template: ` <div loading-area-indicator></div> `,
  standalone: true,
  imports: [MatIconModule, TranslateModule, CommonModule],
})
export class AccountSelectionComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  public location: string = "account-selection";
  constructor(
    private loaderService: LoaderService,
    private languageService: LanguageService,
    private accountService: AccountService,
    private router: Router,
    private authService: AuthService,
  ) {
    this.languageService.currentLanguage$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((language) => {
        this.languageService.updateLanguage(language.value);
      });
  }

  ngOnInit(): void {
    this.loadAccounts();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private loadAccounts(): void {
    this.authService.userInfo$
      .pipe(
        take(1),
        switchMap((userData) => this.resolveAccountStream(userData)),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(({ res, userData }) =>
        this.handleAccountResponse(res, userData),
      );
  }

  private resolveAccountStream(userData: any): Observable<any> {
    const uuid = userData.user?.uuid;
    if (!uuid) return this.handlePermissionError();

    const lastUsedAccountId = this.accountService.getStoredAccount(uuid);

    // If the account request fail get all acounts and the code will use the first one.
    const account$ = lastUsedAccountId
      ? this.accountService
          .getAccountById(lastUsedAccountId)
          .pipe(catchError(() => this.accountService.getAllAccounts("", 1)))
      : this.accountService.getAllAccounts("", 1);

    return account$.pipe(
      map((res) => ({ res, userData })),
      catchError(() => this.handlePermissionError()),
    );
  }

  private handlePermissionError(): Observable<never> {
    this.loaderService.hide();
    this.router.navigate(["/insufficient-permissions"]);
    return EMPTY;
  }

  private handleAccountResponse(res: any, userData): void {
    const uuid = userData.user?.uuid;
    let account: Account;

    if (res instanceof Account) {
      account = res;
    } else {
      account = res.data?.[0];
    }

    if (uuid && account) {
      this.accountService.storeLastUsedAccount(account, uuid);
      this.accountService.selectedAccount = account;
      this.router.navigate(["nconnect-voice", "shortcuts", uuid]);
    } else {
      this.handlePermissionError();
    }

    this.loaderService.hide();
  }
}
